import * as React from "react";
import {Link} from "react-router-dom";
import {lazyInject} from "@athena-portal/core";
import {Types} from "../Types";
import {IPromotionPortalService} from "../services/IPromotionPortalService";
import {finalize} from "rxjs/operators";
import {DateInput} from "../components/DateInput";
import {TextInput} from "../components/TextInput";
import {VehicleDetails} from "../model/VehicleDetails";
import VehicleMakeModelSelector from "../components/VehicleMakeModelSelector";
import {InsuranceContributionRequest} from "../model/InsuranceContributionRequest";
import PopUpTickMessage from "../components/SuccessPopUp";
import {Overlay} from "@athena-portal/components";
import {handleRetailerSubmitLogic} from "../helper/RetailerSubmitHandler";
import { RadioInput } from "../components/RadioInput";
import {EMAIL_REGEX} from "../Constants";

interface IState {
    formData: InsuranceContributionRequest;
    errorResponse: string;
    successResponse: string;
    loadingQueueSize: number;
    isRetailerValid: boolean;
    retailerChecked: boolean;
    vehMakeFound: boolean;
    vehModelFound: boolean;
    showPopUp: boolean;
    showLoader: boolean;
    isEmailValid: boolean;
}

export default class InsuranceContributionPage extends React.Component<{}, IState> {

    @lazyInject(Types.PromotionPortalService)
    private readonly promotionPortalService: IPromotionPortalService;

    constructor(props: {}) {
        super(props);

        this.initializeFormData = this.initializeFormData.bind(this);

        this.state = {
            formData: this.initializeFormData(new InsuranceContributionRequest()),
            errorResponse: "",
            successResponse: "",
            loadingQueueSize: 0,
            isRetailerValid: false,
            retailerChecked: false,
            vehMakeFound: false,
            vehModelFound: false,
            showPopUp: false,
            showLoader: false,
            isEmailValid: false
        };

        this.handleSaleDateChange = this.handleSaleDateChange.bind(this);
        this.handleUsedVehicleChange = this.handleUsedVehicleChange.bind(this);
        this.handleMakeSelected = this.handleMakeSelected.bind(this);
    }

    public render() {
        const {formData, isRetailerValid, showLoader, isEmailValid} = this.state;
        const showFindVehicleButton = formData?.vrn && formData.vrn.trim() !== '' && formData.newVehicle === false;
        const datesInvalid = this.isSaleDateAfterHandoverDate(formData.saleDate, formData.handoverDate);
        const renewalInvalid = this.isRenewalDateNotInFuture(formData.renewalDate);

        return (
            <div className="container mt-4 d-flex justify-content-center insurance-contribution-page">
                <Overlay show={showLoader}/>
                <div className="card p-4">
                    {this.state.errorResponse && (
                        <div className="alert alert-danger alert-dismissible">
                            <button type="button" className="close" onClick={() => this.setState({errorResponse: ""})}
                                    aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                            <strong>Error!</strong> {this.state.errorResponse}
                        </div>
                    )}
                    {this.state.successResponse && (
                        <div className="alert alert-success alert-dismissible">
                            <button type="button" className="close" onClick={() => this.setState({successResponse: ""})}
                                    aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                            <strong>Success!</strong> {this.state.successResponse}
                        </div>
                    )}
                    <h2>Insurance Offers – Request Form</h2>
                    <p>
                        Before completing this form, please ensure that the customer has received the insurance
                        contribution flyer.
                        The flyer contains details on key eligibility, instructions on how to activate cover, and a link
                        to the full
                        terms and eligibility criteria.
                    </p>
                    <p className="text-danger fw-bold">
                        Important note: To enable customers to activate their cover, you must complete this form for all
                        eligible vehicle sales.
                    </p>
                    <form onSubmit={this.handleSubmit}>
                        <TextInput
                            label="Retailer ID"
                            name="retailerCode"
                            value={formData.retailerCode}
                            onChange={this.handleChange}
                            maxLength={10}
                            required={true}
                            className=""
                        />
                        <button
                            type="button"
                            className="default-button mt-2"
                            onClick={this.handleRetailerSubmit}
                            disabled={!formData.retailerCode}
                        >
                            Submit
                        </button>

                        {!formData.retailerCode &&
                            <p className="text-danger mt-1">
                                Please provide valid Retailer ID to carry on.
                            </p>}

                        {this.state.retailerChecked && !isRetailerValid &&
                            <p className="text-danger mt-1">
                                Please check your ID and re-enter the correct details. If you are still having issues
                                please contact Sales Support.
                            </p>}

                        {formData.retailerCode && isRetailerValid && (
                            <>
                                <DateInput
                                    label="Date of Sale"
                                    name="saleDate"
                                    value={formData.saleDate}
                                    onChange={this.handleSaleDateChange}
                                    required={true}
                                />
                                <DateInput
                                    label="Date of Handover"
                                    name="handoverDate"
                                    value={formData.handoverDate}
                                    onChange={this.handleChange}
                                    required={true}
                                />
                                {datesInvalid && (
                                    <p className="text-danger mt-1">
                                        Date of Sale cannot be after the Date of Handover.
                                    </p>
                                )}
                                <RadioInput
                                    label="Is the vehicle new or used?"
                                    id="usedOrNewVehicle"
                                    trueLabel="New"
                                    falseLabel="Used"
                                    value={this.state.formData.newVehicle}
                                    onChange={this.handleUsedVehicleChange}
                                />
                                {this.state.formData.newVehicle != null &&
                                    <>
                                        <TextInput
                                            label="VIN"
                                            name="vin"
                                            value={formData.vin}
                                            onChange={this.handleChange}
                                            maxLength={17}
                                            required={true}
                                        />
                                        <TextInput
                                            label="Registration Number (optional)"
                                            name="vrn"
                                            value={formData.vrn}
                                            onChange={this.handleVrnChange}
                                            maxLength={8}
                                            required={false}
                                        />
                                    </>
                                }
                                {showFindVehicleButton && (
                                    <button
                                        type="button"
                                        className="default-button mb-3"
                                        onClick={this.handleFindVehicle}
                                    >
                                        Find vehicle
                                    </button>
                                )}
                                <VehicleMakeModelSelector
                                    initialMake={formData.make}
                                    initialModel={formData.model}
                                    onMakeSelected={this.handleMakeSelected}
                                    onModelSelected={this.handleModelSelected}
                                    onMakeEdited={this.handleMakeEdited}
                                    onModelEdited={this.handleModelEdited}
                                    disableMakeInput={!this.state.formData.newVehicle}
                                />
                                <DateInput
                                    label="Date of Renewal"
                                    name="renewalDate"
                                    value={formData.renewalDate}
                                    onChange={this.handleChange}
                                    required={true}
                                />
                                {renewalInvalid && (
                                    <p className="text-danger mt-1">
                                        Date of Renewal must be in the future.
                                    </p>
                                )}
                                <TextInput
                                    label="Customer Email"
                                    name="email"
                                    value={formData.email}
                                    onChange={this.handleChange}
                                    maxLength={50}
                                    required={true}
                                />
                                {formData.email && !isEmailValid &&
                                    <p className="text-danger mt-1">
                                        Please check email address and enter valid one.
                                    </p>
                                }
                                <p>
                                    Note: This needs to be the customer’s email in order for them to claim the promotion
                                </p>
                                <div className="mb-3 form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="consentCheckbox"
                                        name="consent"
                                        checked={formData.consent}
                                        onChange={this.handleConsentChange}
                                        required={true}
                                    />
                                    <label className="form-check-label" htmlFor="consentCheckbox">
                                        I confirm the customer has opted into a insurance contribution offer and I have
                                        provided the customer with the insurance contribution Terms &amp; Conditions
                                    </label>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-3 w-100">
                                    <Link to="/">
                                        <button type="button" className="default-button d-block">Back</button>
                                    </Link>
                                    <button type="submit" className="default-button d-block"
                                            disabled={!this.isFormDataValid() || datesInvalid || renewalInvalid}>Submit
                                    </button>
                                </div>
                            </>
                        )}
                        {this.state.showPopUp && (
                            <PopUpTickMessage
                                onClose={() => this.setState({showPopUp: false})}
                            />
                        )}
                    </form>
                </div>
            </div>
        );
    }

    private readonly handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target;

        this.setState(prevState => {
            const nextState: Pick<IState, "formData" | "isRetailerValid" | "retailerChecked"> = {
                formData: {
                    ...prevState.formData,
                    [name]: value
                },
                isRetailerValid: prevState.isRetailerValid,
                retailerChecked: prevState.retailerChecked
            };

            if (name === "retailerCode") {
                nextState.isRetailerValid = false;
                nextState.retailerChecked = false;
            }

            if(name === "email") {
                this.setState({
                    isEmailValid: EMAIL_REGEX.test(value)
                })
            }

            return nextState;
        });
    };

    private readonly handleVrnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target;

        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                [name]: value
            }
        }));
    };

    private readonly handleRetailerSubmit = () => {
        handleRetailerSubmitLogic({
            retailerCode: this.state.formData.retailerCode,
            promotionPortalService: this.promotionPortalService,
            setState: this.setState.bind(this)
        });
    };

    private readonly handleConsentChange = () => {
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                consent: !prevState.formData.consent
            }
        }));
    };

    private readonly handleMakeSelected = (make: string) => {
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                make
            },
            vehMakeFound: true
        }));
    }

    private readonly handleModelSelected = (model: string) => {
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                model
            },
            vehModelFound: true
        }));
    };

    private readonly handleMakeEdited = () => {
        this.setState({
            vehMakeFound: false
        });
    }

    private readonly handleModelEdited = () => {
        this.setState({
            vehModelFound: false
        });
    }

    private readonly handleFindVehicle = () => {
        this.incrementLoadingQueueSize(() => {
            this.setState({showLoader: true});
            this.promotionPortalService.getVehicleDetails(this.state.formData.vrn)
                .pipe(finalize(() => this.decrementLoadingQueueSize()))
                .subscribe(
                    (response: VehicleDetails) => {
                        this.setState(prevState => ({
                            formData: {
                                ...prevState.formData,
                                make: response.make,
                                model: response.model
                            },
                            showLoader: false,
                            vehMakeFound: response.make != null,
                            vehModelFound: response.model != null
                        }));
                    },
                    (error) => {
                        this.setState(prevState => ({
                            formData: {
                                ...prevState.formData,
                                make: "",
                                model: ""
                            },
                            showLoader: false,
                            vehMakeFound: false,
                            vehModelFound: false,
                            errorResponse: "Failed to get vehicle details. Please try again later.",
                            successResponse: ""
                        }));
                    })
        })
    };

    private readonly handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (this.isFormDataValid()) {
            this.incrementLoadingQueueSize(() => {
                this.setState({showLoader: true});
                this.promotionPortalService.saveInsuranceContributionEligibility(this.state.formData)
                    .pipe(finalize(() => this.decrementLoadingQueueSize()))
                    .subscribe(
                        (response) => {
                            this.setState(oldState => ({
                                formData: this.initializeFormData(oldState.formData),
                                showLoader: false,
                                errorResponse: "",
                                successResponse: "Promotion eligibility successfully registered!",
                                isRetailerValid: true,
                                showPopUp: true,
                                vehMakeFound: false,
                                vehModelFound: false
                            }));
                        },
                        (error) => {
                            this.setState({
                                errorResponse: error.response.errorMessage + " Failed to register promotion. Please try again later.",
                                successResponse: "",
                                showLoader: false
                            });
                        }
                    );
            });
        }
    };

    private readonly incrementLoadingQueueSize = (callback?: () => void) => {
        this.setState((prevState: IState) => ({
            loadingQueueSize: prevState.loadingQueueSize + 1
        }), callback);
    }

    private readonly decrementLoadingQueueSize = (callback?: () => void) => {
        if (this.state.loadingQueueSize === 0) {
            return;
        }
        this.setState((prevState: IState) => ({
            loadingQueueSize: prevState.loadingQueueSize - 1
        }), callback);
    }

    private handleSaleDateChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const newValue: string = e.target.value;
        const oldValue: string = this.state.formData.saleDate;

        if (newValue !== oldValue) {
            this.setState(prevState => ({
                formData: {
                    ...prevState.formData,
                    saleDate: newValue
                }
            }));
        }
    }

    private handleUsedVehicleChange(event: any): void {
        const selection: boolean = event.target.value === "true";

        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                newVehicle: selection
            }
        }));
    }

    private isFormDataValid(): boolean {
        const {isRetailerValid, vehModelFound, vehMakeFound, isEmailValid} = this.state;

        return isRetailerValid && vehModelFound && vehMakeFound && isEmailValid;
    }

    private initializeFormData(request: InsuranceContributionRequest): InsuranceContributionRequest {
        request.consent = false;
        request.handoverDate = new Date().toISOString().split("T")[0];
        request.saleDate = "";
        request.vin = "";
        request.email = "";
        request.renewalDate = "";
        request.vrn = "";
        request.make = "";
        request.model = "";
        request.newVehicle = undefined;

        return request;
    }

    private isSaleDateAfterHandoverDate(saleDate: string, handoverDate: string): boolean {
        if (!saleDate || !handoverDate) {
            return false;
        }
        return new Date(saleDate).getTime() > new Date(handoverDate).getTime();
    }

    private isRenewalDateNotInFuture(renewalDate: string): boolean {
        if (!renewalDate) {
            return false;
        }

        const today = new Date().toISOString().slice(0, 10);

        return renewalDate <= today;
    }

}
